/**
 * Phase 261l — Super Admin Adoption Cockpit: Push Engagement tile.
 *
 * Aggregates `pwa_push_event_log` via the Phase 261l backend endpoint and
 * surfaces:
 *
 *   1. Headline KPI strip — total events, dispatched, delivered, delivery
 *      rate, opt-out rate (muted / total).
 *   2. Per-event-kind breakdown — chat / fee / attendance / progress card etc.
 *      with their individual delivery + opt-out rates.
 *   3. Per-persona breakdown — admin / staff / student / parent.
 *   4. "Worst delivery" watchlist — top 10 (event_kind, persona) pairs where
 *      dispatched > 0 but delivery rate is low (silent permission revocations).
 *
 * Honest empty states (NEVER fake numbers):
 *   • events=0 in window → "No push activity captured yet in this window."
 *   • status='error'     → raw error + retry button
 *
 * Reuses the Phase 261d chassis: window selector (7/14/30/60/90), 10-min
 * server-side cache, manual refresh button.
 */
import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  BellRing, RefreshCw, AlertCircle, ShieldAlert, CheckCircle2,
  BarChart3, Users, Inbox, Sparkles,
} from 'lucide-react';
import AcademiesAtRiskCard from '@/components/admin/AcademiesAtRiskCard';

const EVENT_KIND_LABELS = {
  chat_message: 'Direct messages',
  fee_reminder: 'Fee reminders',
  invoice_new: 'New invoices',
  attendance: 'Attendance',
  progress_card: 'Progress cards',
  broadcast: 'Broadcasts',
};

const PERSONA_LABELS = {
  admin: { label: 'Admins / owners', color: 'bg-violet-100 text-violet-800 border-violet-300' },
  staff: { label: 'Coaches / staff', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  student: { label: 'Students', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  parent: { label: 'Parents', color: 'bg-sky-100 text-sky-800 border-sky-300' },
  unknown: { label: 'Unknown', color: 'bg-slate-100 text-slate-700 border-slate-300' },
};

function fmt(n) {
  if (n == null) return '—';
  if (typeof n !== 'number') return String(n);
  return n.toLocaleString();
}

function pct(n) {
  if (n == null || isNaN(n)) return '—';
  return `${n.toFixed(1)}%`;
}

export default function PushEngagementTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [windowDays, setWindowDays] = useState(30);

  const load = useCallback(async (refresh = false) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    try {
      const r = await api.get(
        `/super-admin/adoption/push-engagement?window_days=${windowDays}${refresh ? '&refresh=1' : ''}`
      );
      setData(r.data);
    } catch (e) {
      setData({ status: 'error', error: 'fetch_failed', detail: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [windowDays]);

  useEffect(() => { load(false); }, [load]);

  if (loading) {
    return (
      <Card data-testid="push-engagement-tile-loading">
        <CardContent className="p-5 space-y-3">
          <Skeleton className="h-5 w-56" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  const isError = data?.status === 'error';
  const totals = data?.totals || {};
  const byEvent = data?.by_event_kind || [];
  const byPersona = data?.by_persona || [];
  const worst = data?.worst_delivery_rows || [];
  const noActivityYet = !isError && (totals.events ?? 0) === 0;

  return (
    <Card className="border-l-4 border-l-sky-500" data-testid="push-engagement-tile">
      <CardContent className="p-5 space-y-4">
        {/* ─── Header strip ─── */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="bg-sky-100 dark:bg-sky-900/30 border border-sky-200 dark:border-sky-800 p-1.5 rounded-lg text-sky-700 dark:text-sky-300">
              <BellRing className="h-4 w-4" />
            </div>
            <div>
              <h3 className="text-sm font-bold flex items-center gap-1.5">
                Push Notification Engagement
                {data?.cached && (
                  <Badge variant="outline" className="text-[10px]" data-testid="push-engagement-cached-badge">
                    cached
                  </Badge>
                )}
              </h3>
              <p className="text-[11px] text-muted-foreground">
                Live dispatch volume + delivery rates from <code className="text-[10px]">pwa_push_event_log</code>.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(windowDays)} onValueChange={(v) => setWindowDays(Number(v))}>
              <SelectTrigger className="h-8 w-28 text-xs" data-testid="push-engagement-window-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="60">Last 60 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm" variant="outline" className="h-8 text-xs"
              onClick={() => load(true)} disabled={refreshing}
              data-testid="push-engagement-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* ─── Error state ─── */}
        {isError && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800/60 text-xs text-rose-900 dark:text-rose-100">
            <AlertCircle className="h-4 w-4 mt-0.5 text-rose-600 shrink-0" />
            <div className="space-y-1">
              <p className="font-bold">Failed to load push engagement data.</p>
              <p className="opacity-80">{data?.detail || data?.error || 'Unknown error.'}</p>
            </div>
          </div>
        )}

        {/* ─── Honest empty state ─── */}
        {!isError && noActivityYet && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 text-xs">
            <Sparkles className="h-4 w-4 mt-0.5 text-slate-500 shrink-0" />
            <div className="space-y-1">
              <p className="font-bold text-slate-800 dark:text-slate-100">
                No push activity captured yet in this window.
              </p>
              <p className="text-slate-600 dark:text-slate-300">
                Push events start landing here the moment any user receives a chat
                message, fee reminder, attendance update, or progress card. They'll
                appear within ~10 minutes of being fired (cache TTL).
              </p>
            </div>
          </div>
        )}

        {/* ─── KPI strip ─── */}
        {!isError && !noActivityYet && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Kpi icon={<Inbox className="h-3.5 w-3.5" />} label="Events" value={fmt(totals.events)} testid="kpi-events" />
            <Kpi icon={<BellRing className="h-3.5 w-3.5" />} label="Dispatched" value={fmt(totals.dispatched)} testid="kpi-dispatched" />
            <Kpi icon={<CheckCircle2 className="h-3.5 w-3.5" />} label="Delivered" value={fmt(totals.delivered)} testid="kpi-delivered" />
            <Kpi
              icon={<BarChart3 className="h-3.5 w-3.5" />}
              label="Delivery rate"
              value={pct(totals.delivery_rate_pct)}
              testid="kpi-delivery-rate"
              tone={totals.delivery_rate_pct < 80 ? 'warn' : 'good'}
            />
            <Kpi
              icon={<ShieldAlert className="h-3.5 w-3.5" />}
              label="Opt-out"
              value={pct(totals.opt_out_rate_pct)}
              testid="kpi-opt-out-rate"
              tone={totals.opt_out_rate_pct > 30 ? 'warn' : 'good'}
            />
          </div>
        )}

        {/* ─── Per-event-kind breakdown ─── */}
        {!isError && !noActivityYet && byEvent.length > 0 && (
          <div className="space-y-2" data-testid="push-engagement-by-event">
            <div className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">By event kind</div>
            <div className="border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 dark:bg-slate-900/60 text-[10px] uppercase font-bold text-slate-500 tracking-wider">
                  <tr>
                    <th className="text-left p-2">Event</th>
                    <th className="text-right p-2">Events</th>
                    <th className="text-right p-2">Dispatched</th>
                    <th className="text-right p-2">Delivered</th>
                    <th className="text-right p-2">Muted</th>
                    <th className="text-right p-2">Delivery %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {byEvent.map((r) => (
                    <tr key={r.event_kind} data-testid={`push-engagement-event-${r.event_kind}`}>
                      <td className="p-2 font-medium text-slate-900 dark:text-slate-100">
                        {EVENT_KIND_LABELS[r.event_kind] || r.event_kind}
                      </td>
                      <td className="p-2 text-right tabular-nums">{fmt(r.events)}</td>
                      <td className="p-2 text-right tabular-nums">{fmt(r.dispatched)}</td>
                      <td className="p-2 text-right tabular-nums">{fmt(r.delivered)}</td>
                      <td className="p-2 text-right tabular-nums">{fmt(r.skipped_muted)}</td>
                      <td className={`p-2 text-right tabular-nums font-bold ${(r.delivery_rate_pct ?? 0) < 80 ? 'text-rose-700 dark:text-rose-300' : 'text-emerald-700 dark:text-emerald-300'}`}>
                        {pct(r.delivery_rate_pct)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ─── Per-persona breakdown ─── */}
        {!isError && !noActivityYet && byPersona.length > 0 && (
          <div className="space-y-2" data-testid="push-engagement-by-persona">
            <div className="text-[11px] uppercase font-bold text-slate-500 tracking-wider flex items-center gap-1">
              <Users className="h-3 w-3" /> By persona
            </div>
            <div className="flex flex-wrap gap-2">
              {byPersona.map((r) => {
                const meta = PERSONA_LABELS[r.persona] || PERSONA_LABELS.unknown;
                return (
                  <div
                    key={r.persona}
                    className="border border-slate-200 dark:border-slate-800 rounded-lg p-2.5 text-xs space-y-1 min-w-[140px]"
                    data-testid={`push-engagement-persona-${r.persona}`}
                  >
                    <Badge variant="outline" className={`text-[10px] ${meta.color}`}>
                      {meta.label}
                    </Badge>
                    <div className="space-y-0.5 pt-1">
                      <p className="text-[10px] text-muted-foreground">Events</p>
                      <p className="font-bold tabular-nums">{fmt(r.events)}</p>
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-[10px] text-muted-foreground">Delivery rate</p>
                      <p className={`font-bold tabular-nums ${(r.delivery_rate_pct ?? 0) < 80 ? 'text-rose-700 dark:text-rose-300' : 'text-emerald-700 dark:text-emerald-300'}`}>
                        {pct(r.delivery_rate_pct)}
                      </p>
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-[10px] text-muted-foreground">Opt-out</p>
                      <p className="font-bold tabular-nums">{pct(r.opt_out_rate_pct)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ─── Worst delivery watchlist ─── */}
        {!isError && worst.length > 0 && (
          <div className="space-y-2" data-testid="push-engagement-worst-rows">
            <div className="text-[11px] uppercase font-bold text-slate-500 tracking-wider flex items-center gap-1">
              <ShieldAlert className="h-3 w-3 text-rose-500" /> Watchlist — lowest delivery rates
            </div>
            <div className="border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 dark:bg-slate-900/60 text-[10px] uppercase font-bold text-slate-500 tracking-wider">
                  <tr>
                    <th className="text-left p-2">Event</th>
                    <th className="text-left p-2">Persona</th>
                    <th className="text-right p-2">Dispatched</th>
                    <th className="text-right p-2">Delivered</th>
                    <th className="text-right p-2">Delivery %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {worst.map((r, i) => {
                    const meta = PERSONA_LABELS[r.persona] || PERSONA_LABELS.unknown;
                    return (
                      <tr key={`${r.event_kind}-${r.persona}-${i}`} data-testid={`push-engagement-worst-${i}`}>
                        <td className="p-2 font-medium">
                          {EVENT_KIND_LABELS[r.event_kind] || r.event_kind}
                        </td>
                        <td className="p-2">
                          <Badge variant="outline" className={`text-[10px] ${meta.color}`}>
                            {meta.label}
                          </Badge>
                        </td>
                        <td className="p-2 text-right tabular-nums">{fmt(r.dispatched)}</td>
                        <td className="p-2 text-right tabular-nums">{fmt(r.delivered)}</td>
                        <td className={`p-2 text-right tabular-nums font-bold ${r.delivery_rate_pct < 80 ? 'text-rose-700 dark:text-rose-300' : 'text-emerald-700 dark:text-emerald-300'}`}>
                          {pct(r.delivery_rate_pct)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Phase 261m — Per-academy risk roster (only renders if any rows). */}
        {!isError && (
          <AcademiesAtRiskCard
            rows={data?.by_academy_at_risk || []}
            threshold={data?.risk_threshold_pct}
            minDispatched={data?.min_dispatched}
          />
        )}
      </CardContent>
    </Card>
  );
}

function Kpi({ icon, label, value, testid, tone }) {
  const toneClass = tone === 'warn'
    ? 'border-amber-300 dark:border-amber-700'
    : 'border-slate-200 dark:border-slate-800';
  return (
    <div className={`rounded-lg border ${toneClass} p-2.5 space-y-1`} data-testid={testid}>
      <div className="flex items-center gap-1 text-[10px] uppercase font-bold text-slate-500 tracking-wider">
        {icon}{label}
      </div>
      <p className="text-base font-bold tabular-nums text-slate-900 dark:text-slate-100">{value}</p>
    </div>
  );
}
